import { useEffect, useState } from "react";
import { Eye, EyeOff, Lock, LogIn, UserRound, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/shell/ScopeBar";
import { LOGO } from "@/components/shell/Sidebar";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Deliberately minimal and non-descriptive: the page is reachable by anyone who finds the domain,
 * so it shows only the brand mark and the sign-in form - no business, store or feature information.
 */
export default function Login({ onLoggedIn }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Zapin";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!login.trim() || !password) {
      setError("Enter your login and password.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(login.trim(), password);
      onLoggedIn?.(res.user);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setError(status === 429 ? detail || "Too many attempts. Please wait a few minutes." : status === 401 ? "Incorrect login or password." : "Could not sign in right now. Please try again.");
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="noise min-h-screen relative overflow-hidden flex items-center justify-center p-4 sm:p-8" data-testid="login-page">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full opacity-[0.12]" style={{ background: "radial-gradient(closest-side, hsl(var(--gold)), transparent)" }} />
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10"><ThemeToggle /></div>

      <div className="relative w-full max-w-[400px]">
        <div className="flex flex-col items-center text-center mb-8" data-testid="login-brand">
          <img src={LOGO} alt="Zapin" className="h-20 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-4">Travel Retail Intelligence</div>
        </div>

        <div className="card-surface p-6 sm:p-8">
          <div className="hairline mb-6" />
          <form onSubmit={submit} className="space-y-4" noValidate data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login" className="text-xs text-muted-foreground">Email or username</Label>
              <div className="relative">
                <UserRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="login" data-testid="login-input" autoComplete="username" autoFocus value={login} onChange={(e) => setLogin(e.target.value)} disabled={busy} aria-invalid={!!error} className={cn("pl-9 h-11 bg-secondary/50 border-border/70", error && "border-danger/50")} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs text-muted-foreground">Password</Label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="password" data-testid="password-input" type={show ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} aria-invalid={!!error} className={cn("pl-9 pr-11 h-11 bg-secondary/50 border-border/70", error && "border-danger/50")} />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/40 focus-gold transition-colors duration-200">
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error && (
              <div role="alert" data-testid="login-error" className="flex items-start gap-2 rounded-lg border border-danger/35 bg-danger/10 px-3 py-2 text-sm text-danger">
                <AlertCircle size={15} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full h-11 font-semibold rounded-lg gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-[11px] text-muted-foreground/70" data-testid="login-footer">Authorised users only.</p>
      </div>
    </div>
  );
}
